import { useMutation, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Impor skema dan tipe yang relevan
import { CreateUserFormData } from '../schemas/CreateUserV4Schema';
import { CreateUserV4Response, CreateUserV4ResponseSchema } from '../schemas/GetUsersV4Schema';

const API_ENDPOINT = '/v4/user';

/**
 * Hook kustom untuk membuat pengguna baru (Versi 4).
 * Mengenkapsulasi logika mutasi, termasuk pemanggilan API,
 * validasi respons, dan invalidasi cache.
 * 
 * @returns Hasil dari React Query `useMutation`.
 */
export const useCreateUserV4 = () => {
    const queryClient = useQueryClient();

    return useMutation<CreateUserV4Response, Error, CreateUserFormData>({
        /**
         * Fungsi mutasi yang akan dijalankan saat `mutate()` dipanggil.
         * @param formData - Data pengguna baru yang akan dibuat.
         */
        mutationFn: async (formData: CreateUserFormData): Promise<CreateUserV4Response> => {
            try {
                // Melakukan request POST untuk membuat pengguna baru.
                const response = await privateApi.post(API_ENDPOINT, formData);

                // Validasi struktur data respons dari API.
                const validationResult = CreateUserV4ResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("API response validation failed in useCreateUserV4:", validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server after creating user.");
                }

                // Kembalikan data yang sudah tervalidasi.
                return validationResult.data;
            } catch (error) {
                console.error('Failed to create user (V4):', error);
                // Lemparkan kembali error agar dapat ditangani oleh callback `onError`.
                throw error;
            }
        },

        /**
         * Callback yang dijalankan setelah mutasi berhasil.
         */
        onSuccess: () => {
            /**
             * Menginversi semua query yang kuncinya dimulai dengan ['users', 'v4'].
             * Ini adalah cara yang efisien untuk memberi tahu React Query bahwa semua data
             * yang terkait dengan pengguna (termasuk daftar yang dipaginasi dan difilter)
             * sekarang sudah "stale" (basi) dan perlu di-refetch.
             * 
             * UI (seperti UsersTableV4) akan secara otomatis diperbarui saat
             * query 'useGetUsersV4' berjalan kembali.
             */
            queryClient.invalidateQueries({ queryKey: ['users', 'v4'] });
        },

        /**
         * Callback yang dijalankan jika mutasi gagal.
         * Meskipun error sudah di-log di `mutationFn`,
         * blok ini bisa digunakan untuk logika tambahan jika diperlukan.
         * 
         * @param error - Objek error yang dilempar dari `mutationFn`.
         */
        onError: (_error) => {
            // Error sudah di-log di dalam mutationFn.
            // Blok ini ada untuk kelengkapan, bisa digunakan untuk side-effect lain
            // seperti mengirim log ke layanan eksternal.
            // logErrorToService(error);
        },
    });
};